import { makeStyles } from "tss-react/mui";
import {
  BUTTON_COLORS,
  BUTTON_VARIANTS,
  TYPOGRAPHY_VARIANTS,
} from "@miplatform/constants";
import { ICalendarWorkTemplateBase } from "@miplatform/types";
import { addToDate, formatCountdown } from "@miplatform/utils";
import { mdiAccountPlusOutline } from "@mdi/js";
import {
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  LinearProgress,
  Typography,
  useTheme,
} from "@mui/material";
import { MiButton } from "../MiButton/MiButton";
import { MiModal } from "../MiModal/MiModal";
import { useAutoAssignWorkOrderModalViewModel } from "./useAutoAssignWorkOrderModalViewModel";

export type AutoAssignWorkOrderModalProps = {
  open: boolean;
  calendarSetId?: string;
  workBaseTemplates?: ICalendarWorkTemplateBase[];
  isLoading?: boolean;
  onClose: () => void;
  onConfirm?: (workOrderTemplateIds: string[]) => void | Promise<void>;
};

export const AutoAssignWorkOrderModal = ({
  open,
  calendarSetId,
  workBaseTemplates = [],
  isLoading = false,
  onClose,
  onConfirm,
}: AutoAssignWorkOrderModalProps) => {
  const { classes, theme } = useStyles();
  const muiTheme = useTheme();
  const viewModel = useAutoAssignWorkOrderModalViewModel({
    open,
    calendarSetId,
    workBaseTemplates,
    isLoading,
    onClose,
    onConfirm,
  });

  return (
    <MiModal
      open={open}
      closeModal={viewModel.handleClose}
      width={448}
      titleIcon={mdiAccountPlusOutline}
      leftIconColor={muiTheme.palette.primary.main}
      title="Auto-Assign Work Orders"
      loading={viewModel.isModalLoading}
      actions={[
        {
          name: "Close",
          variant: BUTTON_VARIANTS.TEXT,
          color: BUTTON_COLORS.BLACK,
          onClick: viewModel.handleClose,
        },
        onConfirm
          ? {
              name: "Auto-Assign Work Orders",
              variant: BUTTON_VARIANTS.CONTAINED,
              color: BUTTON_COLORS.PRIMARY,
              disabled: viewModel.isConfirmDisabled,
              onClick: viewModel.handleConfirm,
            }
          : null,
      ].filter((action) => action !== null)}
    >
      <Box className={classes.root}>
        {viewModel.latestOperationQueued && (
          <Typography
            variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
            color={theme.palette.text.secondary}
            mb={2}
          >
            Auto-assignment will start running in{" "}
            {formatCountdown(
              addToDate(
                viewModel.getLatestOperationStatus()?.startedOn,
                10,
                "minutes"
              ),
              new Date().getTime()
            )}
            .
          </Typography>
        )}
        {viewModel.isUnassigningWorkOrders && (
          <Box className={classes.unassigningContainer}>
            <CircularProgress size={16} />
            <Typography
              variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
              fontWeight={theme.fontWeight.semiBold}
            >
              Unassigning existing work orders…
            </Typography>
          </Box>
        )}
        {!viewModel.isUnassigningWorkOrders &&
          viewModel.hasLatestOperation &&
          !viewModel.latestOperationQueued && (
            <Box className={classes.progressContainer}>
              <Box className={classes.progressHeader}>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
                  fontWeight={theme.fontWeight.semiBold}
                >
                  {viewModel.progressTitle}
                </Typography>
                <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
                  {viewModel.progress.processed} of {viewModel.progress.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={viewModel.progress.percentage}
              />
              <Typography
                variant={TYPOGRAPHY_VARIANTS.CAPTION}
                color={theme.palette.text.secondary}
              >
                {viewModel.progress.assigned} assigned ·{" "}
                {viewModel.progress.failed} not assigned
              </Typography>

              <Box className={classes.progressHeader}>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
                  fontWeight={theme.fontWeight.semiBold}
                >
                  Days processed
                </Typography>
                <Typography variant={TYPOGRAPHY_VARIANTS.CAPTION}>
                  {viewModel.dayProgress.current} of{" "}
                  {viewModel.dayProgress.total}
                </Typography>
              </Box>
              <LinearProgress
                variant="determinate"
                value={viewModel.dayProgress.percentage}
              />
            </Box>
          )}

        {!viewModel.latestOperationQueued && workBaseTemplates.length > 0 && (
          <>
            <Typography
              variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
              color={theme.palette.text.secondary}
              mb={2}
            >
              Select which type of work orders you&apos;d like to auto-assign.
            </Typography>

            <Box className={classes.templatesContainer}>
              <Box className={classes.templatesHeader}>
                <Typography
                  variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}
                  fontWeight={theme.fontWeight.semiBold}
                >
                  Work Order Types
                </Typography>
              </Box>

              <Box className={classes.templateList}>
                {workBaseTemplates.map((template) => (
                  <Box key={template.id}>
                    <Typography variant={TYPOGRAPHY_VARIANTS.SUBTITLE2}>
                      {template.name}
                    </Typography>
                    <Typography
                      variant={TYPOGRAPHY_VARIANTS.CAPTION}
                      color={theme.palette.text.secondary}
                    >
                      {viewModel.getTemplateStatusText(template.id)}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>
          </>
        )}
      </Box>
    </MiModal>
  );
};

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
  },
  progressContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  progressHeader: {
    display: "flex",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  unassigningContainer: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(2),
    borderRadius: theme.shape.borderRadius,
    backgroundColor: theme.palette.action.hover,
  },
  templatesContainer: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
    padding: theme.spacing(1),
  },
  templatesHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: theme.spacing(1),
  },
  selectAllButton: {
    height: 24,
    padding: theme.spacing(0, 1),
  },
  templateList: {
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(1),
  },
  templateOption: {
    alignItems: "flex-start",
    margin: 0,
  },
}));
